import React, { useState, useEffect, useMemo } from 'react';  
import { motion } from 'framer-motion';  
import { Search, Filter, Play } from 'lucide-react';  
import { Link } from 'react-router-dom';  
import PropertyCard from './components/PropertyCard';  
import propiedades from './data/propiedades.json';  
import Header from './components/Header';  
import Footer from './components/Footer';

const Home = () => {
  const visibleProps = useMemo(
    () => propiedades.filter(prop => prop.visible !== false),
    []
  );
  const corporateProps = useMemo(
    () => visibleProps.filter(prop => prop.segmento === 'corporativo'),
    [visibleProps]
  );
  const residentialProps = useMemo(
    () => visibleProps.filter(prop => prop.segmento === 'residencial'),
    [visibleProps]
  );

  const [filteredCorporateProps, setFilteredCorporateProps] = useState(corporateProps);  
  const [filteredResidentialProps, setFilteredResidentialProps] = useState(residentialProps);  
  const [searchTerm, setSearchTerm] = useState('');  

  useEffect(() => {  
    const term = searchTerm.toLowerCase();  
    const corporateFiltered = corporateProps.filter(prop =>  
      prop.nombre.toLowerCase().includes(term) ||  
      prop.ubicacion.toLowerCase().includes(term)  
    );  
    const residentialFiltered = residentialProps.filter(prop =>  
      prop.nombre.toLowerCase().includes(term) ||  
      prop.ubicacion.toLowerCase().includes(term) ||  
      prop.tipo.toLowerCase().includes(term)  
    );  
    setFilteredCorporateProps(corporateFiltered);  
    setFilteredResidentialProps(residentialFiltered);  
  }, [searchTerm, corporateProps, residentialProps]);  

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />
      <div className="container mx-auto px-4 py-4 max-w-6xl">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            Propiedades
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Encuentra el espacio perfecto para ti o tu empresa: casa, lotes, oficinas, naves y más en ubicaciones premium.
          </p>
        </motion.div>

      </div>
      <div className="container mx-auto px-4 py-4 max-w-6xl">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            />
          </div>
          <button className="flex items-center gap-2 px-6 py-3 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all">
            <Filter className="w-5 h-5 text-gray-500" />
            Filtros
          </button>
        </div>

        <motion.div
          className="text-center my-12"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Propiedades para Oficinas
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Una colección curada de oficinas, naves y más en ubicaciones premium.
          </p>
        </motion.div>

        <motion.div  
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"  
          initial={{ opacity: 0 }}  
          animate={{ opacity: 1 }}  
          transition={{ staggerChildren: 0.1 }}  
        >  
          {filteredCorporateProps.length > 0 ? (  
            filteredCorporateProps.map((prop) => (  
              <PropertyCard key={prop.id} propiedad={prop} />  
            ))  
          ) : (  
            <p className="col-span-full text-center text-gray-500 py-8">No se encontraron propiedades. Intenta otro término.</p>  
          )}  
        </motion.div>  

        <motion.div  
          className="mt-14"  
          initial={{ opacity: 0, y: 30 }}  
          whileInView={{ opacity: 1, y: 0 }}  
          viewport={{ once: true }}  
          transition={{ duration: 0.5 }}  
        >  
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl px-8 py-10 shadow-2xl flex flex-col lg:flex-row items-center gap-8 text-white">  
            <div className="flex-1 space-y-4">  
              <p className="uppercase text-sm tracking-[0.3em] text-white/80">Experiencia inmersiva</p>  
              <h2 className="text-3xl md:text-4xl font-bold">Tour Virtuales y Recorridos Aéreos</h2>  
              <p className="text-white/90 text-lg">  
                Conoce la zona desde el aire antes de visitarla. Muestra accesos, amenidades y el contexto urbano completo a través de nuestros vuelos 360°.  
              </p>  
              <div className="flex flex-wrap gap-4 mt-4">
                <Link  
                  to="/tours-virtuales"  
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-700 font-semibold rounded-full hover:bg-blue-50 transition-colors"  
                >  
                  Explorar tours virtuales  
                </Link>  
                <Link  
                  to="/recorrido-aereo-1"  
                  className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-full shadow-lg hover:bg-purple-700 hover:shadow-xl hover:scale-105 transition-all"  
                >  
                  <Play className="w-4 h-4 mr-1.5 fill-white animate-pulse" />  
                  Ver demo en vivo  
                </Link>  
              </div>
            </div>  
            <div className="flex-1 w-full">  
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-64 w-full">  
                <img  
                  src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1200&q=80"  
                  alt="Vista aérea de ciudad"  
                  className="w-full h-full object-cover"  
                />  
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />  
                <div className="absolute bottom-4 left-4 text-white">  
                  <p className="text-xs uppercase tracking-[0.4em] text-white/70">Vista aérea</p>  
                  <p className="text-xl font-semibold">Contexto completo de la zona</p>  
                </div>  
              </div>  
              <div className="flex flex-wrap gap-4 text-sm uppercase tracking-wide font-semibold mt-6">  
                <span className="bg-white/10 px-5 py-2.5 rounded-full border border-white/30">Rutas estratégicas</span>  
                <span className="bg-white/10 px-5 py-2.5 rounded-full border border-white/30">Tour aéreo</span>  
                <span className="bg-white/10 px-5 py-2.5 rounded-full border border-white/30">Amenidades destacadas</span>  
              </div>  
            </div>  
          </div>  
        </motion.div>  
      </div>  

      <div className="container mx-auto px-4 pb-16 max-w-6xl">  
        <motion.div
          className="text-center my-12"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Propiedades en Condominio para Vivienda
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Una colección curada de departamentos, casas y lotes residenciales dentro de los mejores desarrollos de condominios. Ideales para habitar o construir bajo esquemas con amenidades premium.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ staggerChildren: 0.1 }}
        >
          {filteredResidentialProps.length > 0 ? (  
            filteredResidentialProps.map((prop) => (  
              <PropertyCard key={prop.id} propiedad={prop} />  
            ))  
          ) : (  
            <p className="col-span-full text-center text-gray-500 py-8">Próximamente más opciones residenciales en condominio.</p>
          )}  
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default Home;
